import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  Box, 
  Alert,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';

/**
 * SaveTemplateDialog Component
 * 
 * A dialog component for saving cases as templates.
 * Allows users to specify template name, description, and version number.
 * 
 * Features:
 * - Template name input (required)
 * - Template description input (optional)
 * - Version number input (defaults to 1)
 * - Form validation
 * - Keyboard support (Enter to submit)
 * - Internationalization support
 * - Consistent styling with the application theme
 */
const SaveTemplateDialog = ({
  open,
  onClose,
  onSave,
  caseData,
  primaryColor = '#5757b0',
  hoverColor = '#7777d0'
}) => {
  const { t } = useTranslation();
  
  // Form state
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [version, setVersion] = useState('1');

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Pre-fill with case title if available
      setTemplateName(caseData?.title ? `${caseData.title} Template` : '');
      setTemplateDescription(caseData?.description || '');
      setVersion('1');
    } else {
      // Clear form when closing
      setTemplateName('');
      setTemplateDescription('');
      setVersion('1');
    }
  }, [open, caseData]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && templateName.trim()) {
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    const trimmedName = templateName.trim();
    const trimmedDescription = templateDescription.trim();
    const versionNumber = parseInt(version) || 1;
    
    if (!trimmedName) {
      // Don't submit if name is empty
      return;
    }

    onSave({
      templateName: trimmedName,
      templateDescription: trimmedDescription,
      version: versionNumber,
      caseId: caseData?.id
    });
    
    handleClose();
  };

  const handleClose = () => {
    setTemplateName('');
    setTemplateDescription('');
    setVersion('1');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 12px 40px rgba(87, 87, 176, 0.2)',
          overflow: 'hidden',
          position: 'relative',
          minHeight: '450px',
          maxHeight: '600px',
        }
      }}
    >
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${primaryColor}, ${hoverColor})`,
        color: 'white',
        fontWeight: '600',
        fontSize: '1.25rem',
        py: 2.5,
        px: 3,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '70px',
        boxSizing: 'border-box',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SaveIcon sx={{ mr: 1.5, fontSize: '1.4rem' }} />
          {t('template.save_as_template') || 'Save as Template'}
        </Box>
        <CloseButton
          onClick={handleClose}
          sx={{
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            p: 0.8,
          }}
        />
      </DialogTitle>
      
      <DialogContent sx={{ 
        "--dialog-content-top-padding": "0px",
        pt: 0, 
        pb: 0, 
        px: 4,
        mt: 0,
        minHeight: '280px',
        background: 'linear-gradient(180deg, rgba(247, 247, 255, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        gap: 2,
      }}>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          label={t('template.name') || 'Template Name'}
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          onKeyPress={handleKeyPress}
          required
          error={!templateName.trim() && templateName !== ''}
          helperText={!templateName.trim() && templateName !== '' ? 'Template name is required' : ''}
          sx={{
            mt: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              minHeight: '56px',
              '& fieldset': {
                borderColor: 'rgba(87, 87, 176, 0.2)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px rgba(87, 87, 176, 0.1)`,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(87, 87, 176, 0.7)',
              fontWeight: '500',
              '&.Mui-focused': {
                color: primaryColor,
              },
            },
          }}
        />

        <TextField
          fullWidth
          variant="outlined"
          label={t('template.description') || 'Template Description'}
          value={templateDescription}
          onChange={(e) => setTemplateDescription(e.target.value)}
          multiline
          rows={3}
          placeholder={t('template.description_placeholder') || 'Describe what this template is for...'}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(87, 87, 176, 0.2)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px rgba(87, 87, 176, 0.1)`,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(87, 87, 176, 0.7)',
              fontWeight: '500',
              '&.Mui-focused': {
                color: primaryColor,
              },
            },
          }}
        />

        <Alert
          severity="warning"
          variant="outlined"
          sx={{
            borderRadius: '10px',
            backgroundColor: 'rgba(255, 244, 229, 0.9)',
            borderColor: 'rgba(255, 183, 77, 0.6)',
            color: '#7d5b00',
            '& .MuiAlert-icon': {
              color: '#7d5b00',
            },
            fontSize: '0.95rem',
            lineHeight: 1.4,
          }}
        >
          {t('template.confidential_warning') || 'Remove all confidential data (paths, API keys, secrets, etc.) before sharing this template with external users.'}
        </Alert>

        <TextField
          fullWidth
          variant="outlined"
          label={t('template.version') || 'Version'}
          type="number"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          inputProps={{ min: 1, step: 1 }}
          sx={{
            maxWidth: '200px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              '& fieldset': {
                borderColor: 'rgba(87, 87, 176, 0.2)',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': {
                borderColor: hoverColor,
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderColor: primaryColor,
                borderWidth: '2px',
                boxShadow: `0 0 0 3px rgba(87, 87, 176, 0.1)`,
              },
            },
            '& .MuiInputLabel-root': {
              color: 'rgba(87, 87, 176, 0.7)',
              fontWeight: '500',
              '&.Mui-focused': {
                color: primaryColor,
              },
            },
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 4, 
        pt: 3,
        gap: 2,
        background: 'rgba(247, 247, 255, 0.3)',
        borderTop: '1px solid rgba(87, 87, 176, 0.1)',
      }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ 
            color: 'rgba(87, 87, 176, 0.8)',
            borderColor: 'rgba(87, 87, 176, 0.3)',
            borderRadius: '8px',
            px: 3,
            py: 1,
            fontWeight: '500',
            textTransform: 'none',
            '&:hover': { 
              backgroundColor: 'rgba(87, 87, 176, 0.05)',
              borderColor: 'rgba(87, 87, 176, 0.5)',
            }
          }}
        >
          {t('close') || 'Cancel'}
        </Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={!templateName.trim()}
          sx={{
            backgroundColor: primaryColor,
            borderRadius: '8px',
            px: 3,
            py: 1,
            fontWeight: '600',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(87, 87, 176, 0.4)',
            '&:hover': { 
              backgroundColor: hoverColor,
              boxShadow: '0 6px 16px rgba(87, 87, 176, 0.5)',
              transform: 'translateY(-1px)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(87, 87, 176, 0.3)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {t('template.save_template') || 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveTemplateDialog;
